/**
 * useSwipeToDismiss — touch gesture hook for swiping a drawer closed.
 *
 * Tracks touchstart / touchmove / touchend on a ref'd element.
 * When the user swipes left past a configurable threshold, fires `onDismiss`.
 * During the swipe, applies a CSS translateX for real-time visual feedback,
 * and snaps back on release if the swipe was too short.
 *
 * Uses a callback ref pattern so it works reliably inside portals (Sheet).
 *
 * Usage:
 *   const swipeRef = useSwipeToDismiss({ onDismiss: () => setOpen(false) });
 *   <div ref={swipeRef}> ... </div>
 */
import { useRef, useCallback } from "react";

const VELOCITY_THRESHOLD = 0.35; // px/ms — fast flick overrides distance

export default function useSwipeToDismiss({
    onDismiss,
    threshold = 60,
    direction = "left",
    enabled = true,
}) {
    const startX = useRef(0);
    const startY = useRef(0);
    const startTime = useRef(0);
    const tracking = useRef(false);
    const cleanupRef = useRef(null);
    const nodeRef = useRef(null);

    const handleTouchStart = useCallback((e) => {
        const touch = e.touches[0];
        startX.current = touch.clientX;
        startY.current = touch.clientY;
        startTime.current = Date.now();
        tracking.current = true;

        const el = nodeRef.current;
        if (el) el.style.transition = "none";
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!tracking.current) return;
        const touch = e.touches[0];
        const dx = touch.clientX - startX.current;
        const dy = touch.clientY - startY.current;

        // Vertical scroll dominates → bail
        if (Math.abs(dy) > Math.abs(dx) * 1.3) {
            tracking.current = false;
            const el = nodeRef.current;
            if (el) { el.style.transform = ""; el.style.transition = ""; el.style.opacity = ""; }
            return;
        }

        const isCorrect = direction === "left" ? dx < 0 : dx > 0;
        const el = nodeRef.current;
        if (!isCorrect) {
            if (el) { el.style.transform = ""; el.style.opacity = ""; }
            return;
        }

        // Clamp horizontal translation
        const cap = threshold * 2.5;
        const clamped = direction === "left" ? Math.max(dx, -cap) : Math.min(dx, cap);

        if (el) {
            el.style.transform = `translateX(${clamped}px)`;
            const progress = Math.min(Math.abs(clamped) / (threshold * 2), 1);
            el.style.opacity = `${1 - progress * 0.3}`;
        }

        // Block page scroll during horizontal drag
        if (Math.abs(dx) > 10) e.preventDefault();
    }, [direction, threshold]);

    const handleTouchEnd = useCallback((e) => {
        if (!tracking.current) return;
        tracking.current = false;

        const touch = e.changedTouches[0];
        const dx = touch.clientX - startX.current;
        const elapsed = Math.max(Date.now() - startTime.current, 1);
        const velocity = Math.abs(dx) / elapsed;
        const isCorrect = direction === "left" ? dx < 0 : dx > 0;
        const dist = Math.abs(dx);

        const el = nodeRef.current;
        if (el) el.style.transition = "transform 180ms ease-out, opacity 180ms ease-out";

        if (isCorrect && (dist >= threshold || velocity >= VELOCITY_THRESHOLD)) {
            // Dismiss — slide off-screen
            if (el) {
                el.style.transform = `translateX(${direction === "left" ? "-110%" : "110%"})`;
                el.style.opacity = "0";
            }
            setTimeout(() => {
                if (onDismiss) onDismiss();
                if (el) { el.style.transform = ""; el.style.opacity = ""; el.style.transition = ""; }
            }, 180);
        } else {
            // Snap back
            if (el) { el.style.transform = ""; el.style.opacity = ""; }
        }
    }, [direction, threshold, onDismiss]);

    /**
     * Callback ref — attaches/detaches listeners whenever the DOM node
     * mounts or unmounts (works reliably with portals).
     */
    const callbackRef = useCallback(
        (node) => {
            // Cleanup previous listeners
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }

            nodeRef.current = node;

            if (!node || !enabled) return;

            node.addEventListener("touchstart", handleTouchStart, { passive: true });
            node.addEventListener("touchmove", handleTouchMove, { passive: false });
            node.addEventListener("touchend", handleTouchEnd, { passive: true });

            cleanupRef.current = () => {
                node.removeEventListener("touchstart", handleTouchStart);
                node.removeEventListener("touchmove", handleTouchMove);
                node.removeEventListener("touchend", handleTouchEnd);
            };
        },
        [enabled, handleTouchStart, handleTouchMove, handleTouchEnd],
    );

    return callbackRef;
}
